/**
 * The alarm shape shared across the COP, and the one ordering every surface
 * agrees on.
 *
 * This lived in `AlarmPanel.tsx` until the in-flow alarm row was replaced by a
 * map-overlay HUD, and that in turn by the top row's bell
 * (`_cop/CopAlertBell.tsx`). Twelve modules import the type and only one
 * imports the component, so the type outlives its hosts: a presentation change
 * must not be able to break `StripBoard`, `DroneMap`'s selection chip, or the
 * telemetry helpers. Two moves in, that has held.
 */

export interface AlarmEntry {
  id: string;
  /** The rule that raised it. Identifies the *condition*, where `id` only
   *  identifies one firing of it — so this is what deduplicates a re-alarm. */
  rule_id?: string | null;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  drone_id: string | null;
  drone_callsign: string | null;
  status: 'active' | 'acknowledged' | 'resolved';
  created_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
}

/** Critical outranks warning outranks info. Unknown severities sort last. */
export const SEVERITY_ORDER: Record<string, number> = { critical: 0, warning: 1, info: 2 };

/**
 * The operator-facing alarm list: resolved alarms drop out, then severity
 * first, unacknowledged before acknowledged within a severity, newest first
 * within that. Returns a new array — the input is not mutated.
 */
export function sortActiveAlarms(alerts: AlarmEntry[]): AlarmEntry[] {
  return alerts
    .filter((a) => a.status !== 'resolved')
    .sort((a, b) => {
      const sa = SEVERITY_ORDER[a.severity] ?? 9;
      const sb = SEVERITY_ORDER[b.severity] ?? 9;
      if (sa !== sb) return sa - sb;
      if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
}
